import type { ConversationDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Archive, ArchiveRestore, Bot, ChevronLeft, Info, MessagesSquare, MoreHorizontal, Pencil, ScrollText } from 'lucide-react';
import { useState } from 'react';
import { ChatScreen } from '@/components/chat/chat-screen';
import { ConversationThread, failedMessage, useConversationLive, effectiveAgentState, type ConversationLive } from '@/components/chat/conversation-thread';
import { talkMentions, useDeskMentions } from '@/components/chat/desk-talk';
import { ModelOnboarding } from '@/components/chat/model-onboarding';
import { ChatWritesSwitch, CONVERSATION_RULES } from '@/components/conversations';
import { ConversationStateBadge } from '@/components/status-badge';
import { ErrorState, LoadingPage } from '@/components/states';
import { TechDetails } from '@/components/tech-details';
import { AgentConversation } from '@/components/work-item/agent-session';
import { ExecutionCard } from '@/components/work-item/tech';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useAgents, useApiMutation, useIsPhone } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { useRealtimeSubscription } from '@/lib/realtime';
import { ToFrontDeskChat } from '@/routes/front-desk';

export function ConversationPage() {
  const { workspaceId, conversationId } = useParams({ from: '/_authed/w/$workspaceId/conversations/$conversationId' });
  useRealtimeSubscription(workspaceId);
  const { query: conv, live } = useConversationLive(conversationId);

  if (conv.isPending) return <LoadingPage />;
  // A chat with VICKI opens at the Front Desk (docs/features/front-desk.md).
  if (conv.data?.kind === 'front_desk') return <ToFrontDeskChat workspaceId={workspaceId} conversationId={conversationId} />;
  if (conv.isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <ErrorState error={conv.error} onRetry={() => void conv.refetch()} />
        <Button asChild variant="link" className="mt-2 px-0">
          <Link to="/w/$workspaceId/desks" params={{ workspaceId }}>
            {t('Go to desks')}
          </Link>
        </Button>
      </div>
    );
  }
  return <ConversationView conv={conv.data} workspaceId={workspaceId} live={live} />;
}

function ConversationView({ conv, workspaceId, live }: { conv: ConversationDetail; workspaceId: string; live: ConversationLive }) {
  const archived = conv.status === 'archived';
  const agents = useAgents(workspaceId);
  const agentModel = agents.data?.find((a) => a.id === conv.agentId)?.effectiveModel ?? null;
  const state = effectiveAgentState(conv, live.awaitingReply) ?? conv.agentState;
  const [view, setView] = useState<'chat' | 'session'>('chat');
  const phone = useIsPhone();
  const navigate = useNavigate();
  // A chat between desks: asked with @ (docs/features/desk-talk.md); in a desk chat @ asks other desks.
  const talk = conv.kind === 'desk_talk';
  const deskMentions = useDeskMentions(workspaceId, conv.deskId);
  const mentions = talk ? talkMentions(conv, null, []) : conv.kind === 'desk' ? deskMentions : undefined;

  const archive = useApiMutation(() => api.conversations.update(conv.id, { status: archived ? 'active' : 'archived' }), {
    invalidate: [qk.conversation(conv.id), conv.deskId ? qk.deskConversations(conv.deskId) : qk.setupConversations(workspaceId)],
    success: archived ? t('Conversation restored') : t('Conversation archived'),
  });

  const thread = (
    <ConversationThread
      className="flex-1"
      conv={conv}
      agentName={conv.agentName}
      workspaceId={workspaceId}
      live={live}
      autoFocus={!phone}
      mentions={mentions}
      placeholder={talk ? t('Ask {name} — or anyone with @ … (Enter to send)', { name: mentions?.fallback?.name ?? conv.agentName }) : undefined}
      intro={
        // On a phone the chat starts with the messages.
        !phone && (
          <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            {talk && conv.peer?.deskId && conv.peer.deskId === conv.deskId
              ? t('The team chat of {desk}: its Lead asks its agents here what it needs to know for a Work Item, and their answers go back into that Work Item. People read along and ask with @.', { desk: conv.deskName ?? '' })
              : talk
                ? t('Questions between {a} and {b}: asked and answered here, no Work Items. People read along and ask with @.', {
                    a: conv.deskName ?? '',
                    b: conv.peer?.name ?? 'VICKI',
                  })
                : CONVERSATION_RULES}
          </div>
        )
      }
      emptyState={
        <div className="py-10 text-center text-sm text-muted-foreground">
          {t('No messages yet. Write below what you’d like to talk about with {agent}.', { agent: conv.agentName })}
        </div>
      }
      footer={
        !phone &&
        conv.execution && (
          <TechDetails className="mt-6" description={t('Container and logs of the last run.')}>
            <ExecutionCard ex={conv.execution} defaultOpen />
          </TechDetails>
        )
      }
      banner={!archived && <ModelOnboarding agentId={conv.agentId} workspaceId={workspaceId} failedText={failedMessage(conv)?.content} />}
      disabledNotice={
        archived ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
            {t('This conversation is archived.')}
            <Button size="sm" variant="outline" onClick={() => archive.mutate()} loading={archive.isPending}>
              <ArchiveRestore /> {t('Restore')}
            </Button>
          </div>
        ) : undefined
      }
    />
  );

  // On a phone the chat is the whole screen (docs/features/mobile.md#chats); the session and archiving are in its menu.
  if (phone && view === 'chat') {
    return (
      <ChatScreen
        title={conv.title}
        subtitle={
          <>
            {conv.agentName}
            {conv.runtime && <> · {conv.runtime}</>}
            {archived && <> · {t('Archived')}</>}
          </>
        }
        onBack={() =>
          void navigate(
            conv.deskId
              ? { to: '/w/$workspaceId/desks/$deskId', params: { workspaceId, deskId: conv.deskId }, search: { tab: 'chat' } }
              : { to: '/w/$workspaceId/start', params: { workspaceId }, search: { chat: conv.id } },
          )
        }
        actions={
          <>
            <ConversationStateBadge state={state} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-10" aria-label={t('More actions')}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onSelect={() => setView('session')}>
                  <ScrollText /> {t('Agent session')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => archive.mutate()} disabled={archive.isPending}>
                  {archived ? <ArchiveRestore /> : <Archive />} {archived ? t('Restore') : t('Archive')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
        toolbar={conv.canWrite && !archived ? <ChatWritesSwitch conv={conv} /> : undefined}
      >
        {thread}
      </ChatScreen>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b bg-background px-4 py-3 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {conv.deskId ? (
              <Link
                to="/w/$workspaceId/desks/$deskId"
                params={{ workspaceId, deskId: conv.deskId }}
                className="inline-flex w-fit items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="size-4" /> {conv.deskName ?? t('Desk')}
              </Link>
            ) : (
              <Link
                to="/w/$workspaceId/start"
                params={{ workspaceId }}
                search={{ chat: conv.id }}
                className="inline-flex w-fit items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="size-4" /> {t('Start (with the draft)')}
              </Link>
            )}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <ChatWritesSwitch conv={conv} />
              <div className="inline-flex rounded-md border p-0.5">
                <Button size="xs" variant={view === 'chat' ? 'secondary' : 'ghost'} onClick={() => setView('chat')}>
                  <MessagesSquare /> {t('Chat')}
                </Button>
                <Button
                  size="xs"
                  variant={view === 'session' ? 'secondary' : 'ghost'}
                  onClick={() => setView('session')}
                  title={t('The agent’s full session: its instructions, what it said and thought, and every command and tool call with the result.')}
                >
                  <ScrollText /> {t('Agent session')}
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => archive.mutate()} loading={archive.isPending}>
                {archived ? <ArchiveRestore /> : <Archive />} {archived ? t('Restore') : t('Archive')}
              </Button>
            </div>
          </div>
          <div className="min-w-0">
            <EditableTitle conv={conv} />
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Bot className="size-3.5" />
                <Link
                  to="/w/$workspaceId/agents/$agentId"
                  params={{ workspaceId, agentId: conv.agentId }}
                  className="text-foreground hover:text-primary hover:underline"
                >
                  {conv.agentName}
                </Link>
                {conv.runtime && <span>· {conv.runtime}</span>}
                {agentModel && <span className="font-mono text-xs">· {agentModel}</span>}
              </span>
              <ConversationStateBadge state={state} />
              {archived && <span className="text-amber-700 dark:text-amber-400">{t('Archived')}</span>}
            </div>
          </div>
        </div>
      </header>

      {view === 'session' ? <ConversationSession conversationId={conv.id} /> : thread}
    </div>
  );
}

/** Every run that answered this chat, as its runtime reported it. */
function ConversationSession({ conversationId }: { conversationId: string }) {
  const transcript = useQuery({
    queryKey: qk.conversationTranscript(conversationId),
    queryFn: () => api.conversations.transcript(conversationId),
  });
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8">
      <Card className="mx-auto max-w-3xl">
        <p className="px-5 pt-4 pb-3 text-[13px] text-muted-foreground">
          {t(
            'The agent’s full session — its instructions, what it said and thought, and every command and tool call with the result. A run ends when the chat has been quiet for a while; the next message starts a new one.',
          )}
        </p>
        <div className="border-t">
          <AgentConversation
            runs={transcript.data}
            isPending={transcript.isPending}
            error={transcript.error}
            onRetry={() => void transcript.refetch()}
            emptyDescription={t('As soon as the agent answers, its full session shows up here.')}
          />
        </div>
      </Card>
    </div>
  );
}

function EditableTitle({ conv }: { conv: ConversationDetail }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(conv.title);
  const save = useApiMutation((title: string) => api.conversations.update(conv.id, { title }), {
    invalidate: [qk.conversation(conv.id), conv.deskId ? qk.deskConversations(conv.deskId) : qk.setupConversations(conv.workspaceId)],
    onSuccess: () => setEditing(false),
  });

  if (editing) {
    const commit = () => {
      const title = value.trim();
      if (!title || title === conv.title) setEditing(false);
      else save.mutate(title);
    };
    return (
      <Input
        value={value}
        autoFocus
        disabled={save.isPending}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-9 text-lg font-semibold"
        aria-label={t('Title')}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setValue(conv.title);
        setEditing(true);
      }}
      className="group inline-flex max-w-full cursor-text items-center gap-2 text-left"
      title={t('Edit title')}
    >
      <h1 className="truncate text-lg font-semibold tracking-tight sm:text-xl">{conv.title || t('Conversation')}</h1>
      <Pencil className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}
